/**
 * BALANCE — every gameplay tunable in one frozen object (docs/GAME_DESIGN.md).
 * Times are in ticks (10 Hz) unless the key says otherwise; distances in map units (mu).
 * The balance harness (tools/harness) is the tool for changing these numbers.
 */

/** Placeholder product name. UI/server must import this, never hard-code the string. */
export const GAME_NAME = 'ScaledAttack';

export const TICK_HZ = 10;
export const DT = 1 / TICK_HZ;

type DeepReadonly<T> = T extends (infer U)[]
  ? readonly DeepReadonly<U>[]
  : T extends object
    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
    : T;

/**
 * Balance-tuning hook (harness only): an object assigned to `globalThis.SA_BALANCE_OVERRIDE`
 * before this module is first imported is deep-merged into the defaults below. The server and
 * client never set it; the harness uses it for `--balance '{"creep":{"pourIn":0.3}}'` batches.
 */
type Plain = { [k: string]: unknown };
function mergeInto(dst: Plain, src: Plain): void {
  for (const [k, v] of Object.entries(src)) {
    const d = dst[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && d && typeof d === 'object' && !Array.isArray(d)) mergeInto(d as Plain, v as Plain);
    else dst[k] = v;
  }
}

function deepFreeze<T>(o: T): DeepReadonly<T> {
  if (o && typeof o === 'object') {
    for (const v of Object.values(o as object)) deepFreeze(v);
    Object.freeze(o);
  }
  return o as DeepReadonly<T>;
}

/**
 * A curated small-lobby region set (§1.14): its regions and capScale, plus the capScale of a hard
 * lobby (`isHardLobby`: hard bots fight a different war, so one table cannot be fair to both).
 */
interface RegionSet {
  regions: string[];
  capScale: Record<string, number>;
  /** capScale in a hard lobby (≥ half the seats hard bots); missing regions use `capScale` */
  capScaleHard?: Record<string, number>;
  /** multiplier on the neutral capture-rate cap (`creep.neutralRate*`): a faster land grab where cores sit far apart */
  neutralRate?: number;
}

const balance = {
  tickHz: TICK_HZ,
  /** 20:00 */
  matchTicks: 12_000,

  phases: {
    /** weapons release at 4:00 (silos, subs, bombers) */
    releaseTick: 2_400,
    /** leader beacon from 12:00, refreshed every 60 s */
    beaconTick: 7_200,
    beaconIntervalTicks: 600,
    /** HOME defence drops from 1.5 to 1.25 at 12:00 */
    homeLateTick: 7_200,
    /** final phase (creep ×1.25) at 17:00 */
    finalTick: 10_200,
    /**
     * Decapitation (§1.11): from 10:00 a capital can be destroyed (nuked to level 0 or its hex
     * captured), which eliminates its player. Before this tick capitals are hardened: a hit
     * leaves them at L1 at worst and the capital hex cannot be captured.
     */
    capitalsExposedTick: 6_000,
  },

  /**
   * Capital second life (§1.11): from 10:00 the first blow that would destroy a capital (ruin or
   * capture) leaves it standing at `criticalLevel`, CRITICAL for everyone to see. A capital that is
   * not hit again for `criticalTicks` recovers; a lethal blow while critical eliminates its player.
   * `criticalTicks` 0 turns the second life off (the first lethal blow eliminates).
   */
  capital: {
    criticalLevel: 1,
    /**
     * the critical window, restarted by every further level-costing hit (≤ 255 s: phase-event byte);
     * 75 s since the strike-economy pass (cheaper missiles made the finisher too easy at 90 s)
     */
    criticalTicks: 750,
    /**
     * hits landing this soon after the capital went critical cost nothing: the rest of the wave (a
     * jittered or multi-attacker wave spreads over seconds) — the defender gets time to brace
     */
    criticalGraceTicks: 100,
    /** going critical makes the capital's brace ready at once (the defender's answer to the next wave) */
    criticalBraceReady: true,
  },

  pop: {
    /** logistic growth g per second */
    growth: 0.03,
    /** growth floor: max(p, seedFrac·capEff) */
    seedFrac: 0.05,
    /** decay per second while p > capEff */
    overDecay: 0.05,
    /** migration coefficient (per second) */
    migration: 0.06,
    focusToward: 1.5,
    focusAway: 0.67,
    /** neutral pop = neutralFrac · capEff (fixed, no growth / diffusion) */
    neutralFrac: 0.3,
    /** starting pop fill of the core (calibrated to ~810/min starting income) */
    startFill: 0.7,
    cityMultCenter: 0.25,
    cityMultRing: 0.08,
    /** capEff = capBase · cityMult · (1 − radCapLoss·rad) */
    radCapLoss: 0.8,
    radHalfLifeS: 60,
  },

  creep: {
    envPerN: 0.15,
    envN0: 2,
    envMin: 0.85,
    envMax: 1.6,
    /** defender's edge */
    defEdge: 1.25,
    resNeutral: 0.5,
    radDef: 0.6,
    homeEarly: 1.5,
    homeLate: 1.25,
    cityPerLevel: 0.15,
    freshMult: 1.5,
    freshTicks: 600,
    phaseFinal: 1.25,
    /** prog per second per unit of (r − 1) */
    progRate: 0.05,
    progMaxExcess: 2,
    /**
     * Neutral resistance: ceiling on prog per second against neutral hexes, `neutralRateEarly`
     * until `neutralRampFrom`, rising linearly to `neutralRateLate` at `neutralRampTo`
     * (paces first contact without leaving remote land neutral for long).
     */
    neutralRateEarly: 0.02,
    /** the cap is this much higher inside the attacker's focus zone (the land race rewards focus) */
    neutralFocusBonus: 1.6,
    neutralRateLate: 0.15,
    /** neutral targets: prog per second up to progRate × min(r − 1, this) (late mop-up is quicker) */
    neutralMaxExcess: 3,
    neutralRampFrom: 1_800,
    neutralRampTo: 4_800,
    progDecay: 0.1,
    minDef: 0.5,
    survivorsEnemy: 0.5,
    survivorsNeutral: 1.0,
    pourIn: 0.15,
    focusIn: 1.5,
    focusOut: 0.8,
    /** focus zone radius in hexes */
    focusRadius: 5,
    focusCooldownTicks: 30,
    focusDistIntervalTicks: 20,
    /** attacker pressure on a hex it touches only through sea links (no land neighbour) */
    seaOnlyMult: 0.5,
    /**
     * Supply lines (§1.12): attacker pressure and the neutral capture cap are multiplied by
     * clamp(1 − perMu·max(0, d − freeMu), min, 1), d = distance (mu) from the hex to the
     * attacker's capital.
     */
    supplyFreeMu: 30,
    supplyPerMu: 0.015,
    supplyMin: 0.35,
  },

  /**
   * Focused-front readout (§1.13, snapshot header): advancing if the zone's pressure ratio is
   * ≥ `advancing` or it nets ≥ `moveRate` hexes / min; losing if it nets ≤ −moveRate, a rival
   * presses into the zone harder than we press out, or the ratio is < `losing` without gains.
   */
  front: {
    advancing: 1.1,
    losing: 0.9,
    /** hexes / min */
    moveRate: 1,
    /** net hexes / min in the focus zone, exponential average over this many ticks */
    rateTicks: 200,
  },

  economy: {
    startCredits: 15_000,
    stipend: 450,
    perAreaW: 1.0,
    /** hexes at or above this radiation give no territory income */
    areaRadMax: 0.5,
    perPop: 0.1,
    cityPerLevel: 50,
    underdogMult: 1.3,
    /** underdog if area share < threshold × average alive player's share */
    underdogThreshold: 0.6,
    easyBotMult: 0.85,
  },

  regions: {
    /**
     * Per start region (8-player matches): multiplier on the capacity of every hex the player
     * owns (pop, pop income and creep strength scale with it). Evens out the real map's unequal
     * hinterlands; keyed by region name, missing = 1. Smaller matches use `regionSets`.
     */
    capScale: { ATLANTIC: 1.511, MEXICO: 1.72, BRAZIL: 2.121, EUROPA: 1.156, VOLGA: 1.008, GUINEA: 1.023, GANGES: 1.167, CHINA: 0.757 } as Record<string, number>,
    /** 8 players, hard lobby (`isHardLobby`): hard bots fight a different war (decapitation campaigns, subs) */
    capScaleHard: { ATLANTIC: 1.63, MEXICO: 1.63, BRAZIL: 1.9, EUROPA: 1.5, VOLGA: 1.29, GUINEA: 1.0, GANGES: 1.25, CHINA: 0.77 } as Record<string, number>,
  },

  /**
   * Curated start regions for matches with fewer than 8 players (§1.14), keyed by player count,
   * each with its own capScale. 2–5 players share one creep continent (Afro-Eurasia) so everyone
   * makes contact; 6 add ATLANTIC alone in the Americas, 7 ATLANTIC + BRAZIL (North / South).
   * Lobby picks are restricted to the set.
   */
  regionSets: {
    2: { regions: ['VOLGA', 'GANGES'], capScale: { VOLGA: 0.871, GANGES: 1.198 }, capScaleHard: { VOLGA: 0.948, GANGES: 1.108 } },
    3: { regions: ['VOLGA', 'GUINEA', 'CHINA'], capScale: { VOLGA: 0.69, GUINEA: 2.0, CHINA: 0.73 }, capScaleHard: { VOLGA: 0.984, GUINEA: 1.524, CHINA: 0.739 }, neutralRate: 2 },
    4: { regions: ['EUROPA', 'VOLGA', 'GANGES', 'CHINA'], capScale: { EUROPA: 0.977, VOLGA: 1.085, GANGES: 1.524, CHINA: 0.962 }, capScaleHard: { EUROPA: 1.026, VOLGA: 1.087, GANGES: 1.431, CHINA: 0.975 } },
    5: { regions: ['EUROPA', 'VOLGA', 'GUINEA', 'GANGES', 'CHINA'], capScale: { EUROPA: 1.297, VOLGA: 1.015, GUINEA: 1.125, GANGES: 1.232, CHINA: 0.808 } },
    6: { regions: ['ATLANTIC', 'EUROPA', 'VOLGA', 'GUINEA', 'GANGES', 'CHINA'], capScale: { ATLANTIC: 0.589, EUROPA: 1.172, VOLGA: 1.204, GUINEA: 1.394, GANGES: 1.176, CHINA: 0.861 } },
    7: { regions: ['ATLANTIC', 'BRAZIL', 'EUROPA', 'VOLGA', 'GUINEA', 'GANGES', 'CHINA'], capScale: { ATLANTIC: 1.295, BRAZIL: 2.269, EUROPA: 1.168, VOLGA: 1.401, GUINEA: 1.532, GANGES: 1.297, CHINA: 0.884 } },
  } as Record<number, RegionSet>,

  start: {
    capitalLevel: 3,
    townLevel: 1,
    radars: 1,
    sams: 3,
    /** SAM triangle distance from the capital (hexes) */
    samRing: 2,
  },

  build: {
    cancelRefund: 0.5,
    maxRad: 0.3,
  },

  radar: { cost: 650, buildTicks: 200, cap: 10, vision: 16 },

  sam: {
    cost: 900,
    buildTicks: 200,
    cap: 16,
    range: 6,
    sensor: 4,
    ammo: 4,
    /** one interceptor slow-reloads every `reloadTicks`; a bought one goes straight into the magazine */
    reloadTicks: 300,
    roundCost: 200,
    interceptorTicks: 30,
    pk: { icbm: 0.4, slbm: 0.32, bomber: 0.5, fighter: 0.5 },
    /** bombers fly low: SAMs (land and carrier) engage them only this close (mu) */
    bomberRange: 3,
    /** a land SAM that fires radiates: visible to every player this long after its last shot */
    exposedTicks: 300,
    /** SILENT (emissions control): engagement range × this, cued targets only, never exposed */
    silentRangeMult: 0.5,
  },

  /**
   * The silo is the investment, its missiles are cheap (strike-economy pass): a silo carries most
   * of the cost, loads a missile in 10 s and ripple-fires its magazine (one launch per 3 s), so two
   * silos put a 6-warhead salvo in the air within seconds.
   */
  silo: {
    cost: 3_000,
    buildTicks: 450,
    cap: 6,
    magazine: 4,
    missileCost: 800,
    loadTicks: 100,
    cooldownTicks: 30,
    /** silo shows as `exposed` after a launch */
    exposedTicks: 300,
  },

  city: {
    /**
     * A new city (L1) is an investment like a level (city-economy pass; was 2,000, a 43 min payback):
     * on a full, well-placed hex it earns ~50–55/min (cityPerLevel · fill + the pop its capacity holds).
     */
    cost: 500,
    buildTicks: 450,
    /** min axial distance to any other city */
    minSpacing: 3,
    /**
     * Upgrade L→L+1 costs `upgradeCost[L − 1]` (use `cityUpgradeCost(L)`): L1→L2 and L2→L3 pay back
     * in ~6–10 min of the level's income (+50/min at full pop, plus capacity); L3→L4 and L4→L5
     * mostly buy survival (each level is one more hit to absorb; capital levels are its hit points).
     */
    upgradeCost: [300, 500, 2_700, 4_800],
    upgradeTicks: 300,
    maxLevel: 5,
    vision: 3,
  },

  airbase: {
    cost: 1_800,
    buildTicks: 400,
    cap: 3,
    hangar: 4,
    startFighters: 2,
    scrambleRange: 30,
    scrambleCount: 2,
    scrambleCheckTicks: 10,
  },

  fighter: {
    cost: 500,
    buildTicks: 150,
    /** mu per second */
    speed: 2.0,
    radius: 50,
    stationTicks: 1_200,
    vision: 8,
    aams: 2,
    pkBomber: 0.6,
    pkFighter: 0.5,
    engageRange: 3,
    shotCooldownTicks: 10,
    rearmTicks: 200,
    /** intercept-mission heading refresh */
    steerTicks: 10,
  },

  bomber: {
    cost: 1_200,
    buildTicks: 250,
    speed: 1.0,
    radius: 80,
    vision: 4,
    rearmCost: 300,
    rearmTicks: 200,
    bombFallTicks: 10,
  },

  aircraft: {
    /** distance at which an aircraft lands on / is at its home base */
    landRadius: 0.6,
  },

  sub: {
    cost: 3_200,
    buildTicks: 500,
    cap: 3,
    speed: 0.7,
    slbms: 2,
    slbmCost: 1_000,
    slbmLoadTicks: 400,
    slbmRange: 70,
    torps: 4,
    torpRange: 3,
    torpPk: 0.5,
    torpCooldownTicks: 50,
    /** free torpedo restock while docked at an own port */
    torpRestockTicks: 100,
    sonar: 6,
    hp: 1,
    exposedTicks: 300,
  },

  carrier: {
    cost: 2_500,
    buildTicks: 600,
    cap: 2,
    speed: 0.5,
    radar: 14,
    sonar: 8,
    samRange: 6,
    samAmmo: 8,
    samReloadTicks: 300,
    samPk: 0.45,
    fighters: 2,
    hp: 3,
  },

  missiles: {
    icbm: { base: 30, perMu: 0.2, minS: 35, maxS: 90, bend: 0.18, boostTau: 0.65, boostU: 0.8 },
    slbm: { base: 15, perMu: 0.2, bend: 0.08 },
    /** ICBM + SLBM in flight per player */
    inFlightCap: 100,
  },

  nukes: {
    /** by ring 0, 1, 2 */
    popKill: [0.9, 0.65, 0.25],
    rad: [1.0, 0.7, 0.3],
    cityLevelLoss: [2, 1, 0],
    shipKillRadius: 2.5,
  },

  vision: {
    /**
     * every nation's borders are public: hex owners reach every player everywhere (fog still hides
     * units, pop and radiation outside the team's sensors)
     */
    publicBorders: true,
    radar: 16,
    carrierRadar: 14,
    fighter: 8,
    sam: 4,
    city: 3,
    bomber: 4,
    subSonar: 6,
    carrierSonar: 8,
    /** radars (land + carrier) see submerged subs within this range */
    shortRadar: 2,
    hintJitter: 6,
    hintBearingJitterDeg: 15,
    hintSpreadDeg: 12,
    detonationJitter: 3,
  },

  /**
   * Team layouts (§1.14): in teams mode with this many players and teams, each team (by ascending
   * team id) plays these start regions — measured splits where neither side owns a continent
   * (default slot order gave 4v4 91/9 and 2v2 100/0). Keyed `${players}x${teams}`.
   */
  teamLayouts: {
    '8x2': [
      ['MEXICO', 'BRAZIL', 'EUROPA', 'CHINA'],
      ['ATLANTIC', 'VOLGA', 'GUINEA', 'GANGES'],
    ],
    '4x2': [
      ['EUROPA', 'VOLGA'],
      ['GANGES', 'CHINA'],
    ],
  } as Record<string, string[][]>,

  /**
   * Capacity multiplier per team of a team layout (same key and team order as `teamLayouts`), on
   * top of the region's capScale (`teamCapMult`): the region tables are fitted for FFA lobbies, and
   * at 1 / 1 the layouts measured 4v4 21 / 79 and 2v2 41–44 / 56–59 (city-economy pass). Swapping
   * which seats play which group changed nothing (20 / 80, 44 / 56): the lean is the layout's, not
   * the seat order's. Fitted with the harness (`tools/harness/experiments/city-batch.sh` tm*); refitted for the
   * 60-hex cores (start-territory pass, `start-batch.sh` tm* / tt*): 4v4 56 / 44, 2v2 54 / 46.
   */
  teamCapMult: {
    '8x2': [1.027, 0.973],
    '4x2': [0.995, 1.005],
  } as Record<string, number[]>,

  /**
   * Brace (§1.15): an own city braces for 20 s — blast pop loss on its hex and the ring around
   * it is multiplied by `popKillMult`, and hits during the window cost the city no levels.
   */
  brace: {
    cost: 300,
    ticks: 200,
    cooldownTicks: 900,
    popKillMult: 0.5,
    /** hexes around the city that share the pop protection */
    radius: 1,
  },

  alliance: {
    requestTicks: 300,
    breakNoTargetTicks: 300,
    breakNoAllyTicks: 1_800,
  },

  /**
   * Diplomacy beyond alliances (§2): a ceasefire is a timed mutual non-aggression (no creep pressure,
   * no missile / bomber orders between the two) that either side may break early — announced to everyone,
   * with the alliance's 30 s no-strike lock on the breaker and no new ceasefire for 3 min. Gifts move
   * credits between nations; a mark points our ally / teammates at a nation.
   */
  diplomacy: {
    ceasefireTicks: 1_800,
    ceasefireRequestTicks: 300,
    ceasefireBreakNoTargetTicks: 300,
    ceasefireBreakCooldownTicks: 1_800,
    giftAmounts: [500, 1_000, 2_500],
    markCooldownTicks: 50,
  },

  /**
   * Score (§1.9) = round(1000 · ((1 − killWeight) · (½ areaShare + ½ popShare) + killWeight · killShare)),
   * killShare = pop this player's warheads killed on rivals' land ÷ pop killed on rivals' land by
   * everyone (DEFCON's megadeaths: the war counts, not only the land).
   */
  scoring: {
    killWeight: 0.15,
  },

  victory: {
    dominationShare: 0.65,
    dominationTicks: 600,
    elimMinHexes: 8,
  },

  stats: {
    curveIntervalTicks: 300,
  },
};

const override = (globalThis as { SA_BALANCE_OVERRIDE?: unknown }).SA_BALANCE_OVERRIDE;
if (override && typeof override === 'object') mergeInto(balance as unknown as Plain, override as Plain);

export type Balance = DeepReadonly<typeof balance>;
export const BALANCE: Balance = deepFreeze(balance);
